import React, { useState, useRef } from 'react';
import classNameBind from 'classnames/bind';
import styles from './SplitList.module.css';

const bindClasses = classNameBind.bind(styles);
const SIZE_AFTER_ACCORDION_COLLAPSE = 28;
const RESIZE_BUFFER_PX = 50;

const SplitList = ({ children, sectionHeightLocalStorageName = 'pp_SplitList_sectionHeight' }) => {
  const [isFirstSectionClosed, setIsFirstSectionClosed] = useState(
    !JSON.parse(localStorage.getItem(children[0]?.props?.isOpenLocalStorageName || '') || 'true'),
  );
  const [isSecondSectionClosed, setIsSecondSectionClosed] = useState(
    !JSON.parse(localStorage.getItem(children[1]?.props?.isOpenLocalStorageName || '') || 'true'),
  );
  const [sectionHeight, setSectionHeight] = useState(JSON.parse(localStorage.getItem(sectionHeightLocalStorageName) || '0.5'));
  const wrapper = useRef();
  const firstSection = useRef();
  const resizeHandler = useRef();
  let startYPosition = null;
  let startHeight = null;
  let currentHeight = null;
  let currentHeightPx = null;

  const doResize = (e) => {
    e.preventDefault();
    currentHeightPx = startHeight + e.clientY - startYPosition;

    if (currentHeightPx >= RESIZE_BUFFER_PX && currentHeightPx <= wrapper.current.offsetHeight - RESIZE_BUFFER_PX) {
      currentHeight = currentHeightPx / wrapper.current.offsetHeight;
      setSectionHeight(currentHeight);
    }
  };

  const stopResize = () => {
    localStorage.setItem(sectionHeightLocalStorageName, currentHeight);
    window.removeEventListener('mousemove', doResize, false);
    window.removeEventListener('mouseup', stopResize, false);
  };

  const initResize = (e) => {
    e.preventDefault();
    if (!firstSection.current || isFirstSectionClosed || isSecondSectionClosed) return;

    startYPosition = e.clientY;
    startHeight = firstSection.current.offsetHeight;

    window.addEventListener('mousemove', doResize, false);
    window.addEventListener('mouseup', stopResize, false);
  };

  const getFirstSectionHeight = () => {
    if (isFirstSectionClosed) {
      return `${SIZE_AFTER_ACCORDION_COLLAPSE}px`;
    }
    if (isSecondSectionClosed) {
      return `calc(100% - ${SIZE_AFTER_ACCORDION_COLLAPSE}px)`;
    }

    return `${sectionHeight * 100}%`;
  };

  const extendMainFolderOnClickAndRenderChild = sectionNumber => (
    children[sectionNumber].type.displayName === 'MainFolder'
      ? {
        ...children[sectionNumber],
        props: {
          ...children[sectionNumber].props,
          onAccordionClick: (isOpening) => {
            if (children[sectionNumber].props.onAccordionClick) children[sectionNumber].props.onAccordionClick(isOpening);
            if (sectionNumber) {
              setIsSecondSectionClosed(!isOpening);
            } else {
              setIsFirstSectionClosed(!isOpening);
            }
          },
        },
      }
      : children[sectionNumber]
  );

  return (
    <div
      className={bindClasses('wrapper')}
      data-ppuc-id="Sidebar.SplitList"
      ref={wrapper}
    >
      <div
        style={{ maxHeight: getFirstSectionHeight(), minHeight: isFirstSectionClosed ? '0' : `${RESIZE_BUFFER_PX}px` }}
        ref={firstSection}
        className={bindClasses('section')}
      >
        { extendMainFolderOnClickAndRenderChild(0) }
      </div>
      <div
        ref={resizeHandler}
        onMouseDown={e => initResize(e)}
        className={bindClasses('resize-handler', { disabled: isFirstSectionClosed || isSecondSectionClosed })}
        data-ppuc-id="Sidebar.resize"
        role="button"
        tabIndex={0}
      >
        <div className={bindClasses('resize-line')} />
      </div>
      <div
        style={{ flex: '1' }}
        className={bindClasses('section')}
      >
        { extendMainFolderOnClickAndRenderChild(1) }
      </div>
    </div>
  );
};

export default SplitList;
